import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  minDate?: string;
  error?: boolean;
  placeholder?: string;
  label?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  minDate,
  error,
  placeholder,
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState(
    value ? new Date(value) : new Date()
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const daysInMonth = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth(),
    1
  ).getDay();

  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  const prevMonth = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day: number) => {
    const year = displayMonth.getFullYear();
    const month = String(displayMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateString = `${year}-${month}-${dayStr}`;
    onChange(dateString);
    setIsOpen(false);
  };

  const isDateDisabled = (day: number) => {
    if (!minDate) return false;
    const date = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), day);
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < min;
  };

  const isDateSelected = (day: number) => {
    if (!value) return false;
    const [year, month, dayStr] = value.split('-').map(Number);
    return (
      year === displayMonth.getFullYear() &&
      month === displayMonth.getMonth() + 1 &&
      dayStr === day
    );
  };

  const formatDisplayDate = () => {
    if (!value) return '';
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const days = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
  const months = [
    'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
    'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
  ];

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-3 bg-gray-900 border ${
          error ? 'border-red-500' : 'border-gray-600'
        } text-white rounded-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 flex items-center justify-between hover:border-gray-500 transition-colors`}
      >
        <span className={value ? 'text-white' : 'text-gray-500'}>
          {value ? formatDisplayDate() : placeholder || 'Välj datum'}
        </span>
        <CalendarIcon size={20} className="text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 bg-gray-800 border border-gray-700 rounded-none shadow-xl w-full min-w-[320px]">
          <div className="flex justify-between items-center bg-purple-600 text-white p-3 rounded-t-lg">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <h3 className="text-sm font-bold">
              {months[displayMonth.getMonth()]} {displayMonth.getFullYear()}
            </h3>
            <button
              type="button"
              onClick={nextMonth}
              className="p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="p-3">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {days.map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-400 py-1">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: adjustedFirstDay }).map((_, index) => (
                <div key={`empty-${index}`} className="h-9" />
              ))}

              {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1;
                const disabled = isDateDisabled(day);
                const selected = isDateSelected(day);

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => !disabled && handleDateClick(day)}
                    disabled={disabled}
                    className={`
                      h-9 flex items-center justify-center rounded-none text-sm font-medium transition-colors
                      ${disabled ? 'text-gray-600 cursor-not-allowed' : 'hover:bg-gray-700'}
                      ${selected ? 'bg-purple-600 text-white' : 'text-gray-300'}
                    `}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
